import { SupportingTableId } from './supporting-table-shared.model';

export enum GridSearchMode {
  ilike = 'ilike',
  hybrid = 'hybrid',
}

export enum GridResourceId {
  Products = 'products',
  SupportingTableAgencyCodeNameMapping = 'supporting-table:agency-code-name-mapping',
  SupportingTableAgentSuppressions = 'supporting-table:agent-suppressions',
  SupportingTableAgencyNtp = 'supporting-table:agency-ntp',
  SupportingTableNtpMgaTerminatedAgents = 'supporting-table:ntp-mga-terminated-agents',
  SupportingTableAgencyRmd = 'supporting-table:agency-rmd',
  SupportingTableForestersTable = 'supporting-table:foresters-table',
  SupportingTableMgaTable = 'supporting-table:mga-table',
  SupportingTableNlgProdNameTable = 'supporting-table:nlg-prod-name-table',
  SupportingTableAgentMgaCorrections = 'supporting-table:agent-mga-corrections',
  SupportingTableCarriersTable = 'supporting-table:carriers-table',
  SupportingTableGpmEquisMgaUpdate = 'supporting-table:gpm-equis-mga-update',
  ReportingImportInvalidRows = 'reporting-import:invalid-rows',
  ReportingImportNormalizationIssues = 'reporting-import:normalization-issues',
  MasterPaid = 'master-dataset:master-paid',
  MasterSubmit = 'master-dataset:master-submit',
}

export const GRID_RESOURCE_IDS = Object.values(GridResourceId) as GridResourceId[];
const GRID_RESOURCE_ID_SET = new Set<GridResourceId>(GRID_RESOURCE_IDS);

export const SUPPORTING_TABLE_GRID_RESOURCE_BY_ID: Readonly<Record<SupportingTableId, GridResourceId>> = {
  [SupportingTableId.AgencyCodeNameMapping]: GridResourceId.SupportingTableAgencyCodeNameMapping,
  [SupportingTableId.AgentSuppressions]: GridResourceId.SupportingTableAgentSuppressions,
  [SupportingTableId.AgencyNtp]: GridResourceId.SupportingTableAgencyNtp,
  [SupportingTableId.NtpMgaTerminatedAgents]: GridResourceId.SupportingTableNtpMgaTerminatedAgents,
  [SupportingTableId.AgencyRmd]: GridResourceId.SupportingTableAgencyRmd,
  [SupportingTableId.ForestersTable]: GridResourceId.SupportingTableForestersTable,
  [SupportingTableId.MgaTable]: GridResourceId.SupportingTableMgaTable,
  [SupportingTableId.NlgProdNameTable]: GridResourceId.SupportingTableNlgProdNameTable,
  [SupportingTableId.AgentMgaCorrections]: GridResourceId.SupportingTableAgentMgaCorrections,
  [SupportingTableId.CarriersTable]: GridResourceId.SupportingTableCarriersTable,
  [SupportingTableId.GpmEquisMgaUpdate]: GridResourceId.SupportingTableGpmEquisMgaUpdate,
};

const SUPPORTING_TABLE_ID_BY_GRID_RESOURCE_ID = new Map<GridResourceId, SupportingTableId>(
  Object.entries(SUPPORTING_TABLE_GRID_RESOURCE_BY_ID).map(([supportingTableId, resourceId]) => [
    resourceId,
    supportingTableId as SupportingTableId,
  ]),
);

const SUPPORTING_TABLE_GRID_RESOURCE_IDS = new Set<GridResourceId>(Object.values(SUPPORTING_TABLE_GRID_RESOURCE_BY_ID));

export enum GridColumnDataTypeEnum {
  String = 'string',
  Number = 'number',
  Date = 'date',
  DateTime = 'datetime',
  Boolean = 'boolean',
}

export type GridColumnDataType = 'string' | 'number' | 'date' | 'datetime' | 'boolean';

export interface GridColumnDefinition {
  field: string;
  dbField: string;
  dataType: GridColumnDataType;
  required?: boolean;
  editable?: boolean;
  allowFiltering?: boolean;
  allowSorting?: boolean;
  searchable?: boolean;
  searchMode?: GridSearchMode;
  searchThreshold?: number;
}

export interface GridSortDescriptor {
  selector: string;
  desc?: boolean;
}

export interface GridResourceDefinition {
  id: GridResourceId;
  keyField: string;
  tableName: string;
  importIdField?: string | null;
  columns: GridColumnDefinition[];
  defaultSort?: GridSortDescriptor[];
  defaultSearchMode?: GridSearchMode;
  defaultSearchThreshold?: number;
  selectFields?: string[];
}

export function getGridResourceIdBySupportingTableId(supportingTableId: SupportingTableId): GridResourceId {
  return SUPPORTING_TABLE_GRID_RESOURCE_BY_ID[supportingTableId];
}

export function getSupportingTableIdByGridResourceId(resourceId: GridResourceId): SupportingTableId | null {
  return SUPPORTING_TABLE_ID_BY_GRID_RESOURCE_ID.get(resourceId) ?? null;
}

export function isSupportingTableGridResourceId(resourceId: GridResourceId): boolean {
  return SUPPORTING_TABLE_GRID_RESOURCE_IDS.has(resourceId);
}

export function isGridResourceId(value: string): value is GridResourceId {
  return GRID_RESOURCE_ID_SET.has(value as GridResourceId);
}
